import React from "react";
import { BarChart3, Info } from "lucide-react";
import { STATUS_COLOR } from "../data/kumbhGeo";

/* The numbers behind the map: every sector, every parameter, ranked.
 *
 * A command map is read at a glance; a briefing is read line by line. This
 * is the second one — the table an officer reads out, or screenshots into a
 * situation report. So it shows raw counts alongside percentages, because
 * "92%" and "92,140 of 100,000" answer different questions, and headroom in
 * absolute people is what determines whether another 20,000 can be let in.
 */
export default function SectorTable({ lang = "en", rows, totals, onFocus }) {
  const L = (o) => o?.[lang] ?? o?.en ?? "";
  const n = (v) => (v == null ? "—" : v.toLocaleString("en-IN"));

  return (
    <div className="border-t border-slate-200">
      <section className="px-3 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <BarChart3 size={12} className="text-slate-500" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
            {lang === "mr" ? "सेक्टर क्रमवारी" : "Sector Ranking"}
          </span>
          <span className="ml-auto text-[8px] font-black px-1.5 py-0.5 rounded border bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300">
            SIM
          </span>
        </div>

        {/* Theatre totals — the denominators every row below sits inside. */}
        <div className="grid grid-cols-2 gap-1.5 mb-2.5">
          <Tile
            label={lang === "mr" ? "एकूण यात्रेकरू" : "Pilgrims tracked"}
            value={n(totals.pilgrims)}
            sub={`${lang === "mr" ? "क्षमता" : "of"} ${n(totals.capacity)}`}
          />
          <Tile
            label={lang === "mr" ? "उपलब्ध जागा" : "Headroom"}
            value={n(totals.headroom)}
            sub={`${totals.occupancyPct}% ${lang === "mr" ? "भरले" : "full"}`}
            tone={totals.occupancyPct >= 88 ? "red" : totals.occupancyPct >= 70 ? "amber" : "green"}
          />
          <Tile
            label={lang === "mr" ? "झोन (हि/अं/ला)" : "Zones (G/A/R)"}
            value={`${totals.sectorsGreen}/${totals.sectorsAmber}/${totals.sectorsRed}`}
            sub={`${totals.sectors} ${lang === "mr" ? "एकूण" : "total"}`}
          />
          <Tile
            label={lang === "mr" ? "सक्रिय घटना" : "Active incidents"}
            value={n(totals.incidentsActive)}
            sub={`${totals.incidentsCritical} ${lang === "mr" ? "गंभीर" : "critical"}`}
            tone={totals.incidentsCritical > 0 ? "red" : undefined}
          />
        </div>

        {/* Mapped-asset counts — what the dataset actually contains. */}
        <div className="flex flex-wrap gap-1 mb-2.5">
          <Chip label={lang === "mr" ? "घाट" : "Ghats"} value={totals.ghats} />
          <Chip label={lang === "mr" ? "प्रवेश" : "Entry"} value={totals.entryGates} />
          <Chip label={lang === "mr" ? "निर्गमन" : "Exit"} value={totals.exitGates} />
          <Chip label={lang === "mr" ? "सुविधा" : "Facilities"} value={totals.facilities} />
          <Chip label={lang === "mr" ? "एकूण वस्तू" : "Features"} value={totals.mappedFeatures} />
        </div>

        {/* Ranked rows */}
        <div className="space-y-1">
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onFocus({ coord: r.centre, zoom: 14 })}
              className="w-full text-left rounded-lg border border-slate-200 bg-white p-2 hover:border-slate-400 transition-colors"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black text-slate-400 tabular-nums w-4">
                  #{r.rank}
                </span>
                <span
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ background: STATUS_COLOR[r.status] }}
                />
                <span className="text-[11px] font-bold text-slate-800 truncate flex-1">
                  {L(r.name)}
                </span>
                {r.riskScore != null && (
                  <span className="text-[11px] font-black text-slate-900 tabular-nums">
                    {r.riskScore}
                  </span>
                )}
              </div>

              {/* Raw counts, because a percentage alone cannot tell you how
                  many more people a sector can take. */}
              <div className="mt-1 text-[10px] text-slate-600 tabular-nums">
                {n(r.current)} / {n(r.capacity)}
                <span className="font-bold text-slate-800"> · {r.occupancyPct}%</span>
                <span className="text-slate-500">
                  {" "}· {lang === "mr" ? "जागा" : "free"} {n(r.headroom)}
                </span>
              </div>

              {/* Every parameter, so nothing needs to be inferred. */}
              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[9px] text-slate-500 tabular-nums">
                <span>{lang === "mr" ? "घाट" : "ghats"} {r.ghats}</span>
                <span>{lang === "mr" ? "प्र" : "in"} {r.entryGates}</span>
                <span>{lang === "mr" ? "नि" : "out"} {r.exitGates}</span>
                <span>{lang === "mr" ? "सुविधा" : "fac"} {r.facilities}</span>
                {r.incidents > 0 && (
                  <span className="text-red-600 font-bold">
                    {lang === "mr" ? "घटना" : "inc"} {r.incidents}
                  </span>
                )}
                {r.netPerMin != null && r.netPerMin !== 0 && (
                  <span className={r.netPerMin > 0 ? "text-orange-600 font-bold" : "text-emerald-600"}>
                    {r.netPerMin > 0 ? "+" : ""}{r.netPerMin}/min
                  </span>
                )}
                {r.minutesToCritical != null && r.minutesToCritical > 0 && (
                  <span className="font-bold text-slate-700">T−{r.minutesToCritical}m</span>
                )}
                {r.demandRatio != null && (
                  <span className={r.demandRatio > 1 ? "text-orange-700 font-bold" : ""}>
                    {lang === "mr" ? "द्वार" : "gate"} {r.demandRatio}×
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        <p className="mt-2 flex items-start gap-1 text-[9px] leading-snug text-slate-400">
          <Info size={9} className="flex-shrink-0 mt-0.5" />
          {lang === "mr"
            ? "संख्या नकाशावरील नोंदवलेल्या वस्तूंची आहे — नाशिकमध्ये प्रत्यक्ष किती आहेत याची नाही."
            : "Counts are of objects in this dataset, not of what exists in Nashik. Occupancy and flow figures are simulated."}
        </p>
      </section>
    </div>
  );
}

function Tile({ label, value, sub, tone }) {
  const toneCls =
    tone === "red" ? "text-red-700" :
    tone === "amber" ? "text-amber-700" :
    tone === "green" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2 py-1.5">
      <div className={`text-[14px] font-black leading-none tabular-nums ${toneCls}`}>{value}</div>
      <div className="text-[9px] text-slate-500 mt-0.5 leading-tight">{label}</div>
      {sub && <div className="text-[8px] text-slate-400 tabular-nums">{sub}</div>}
    </div>
  );
}

function Chip({ label, value }) {
  return (
    <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] text-slate-600 tabular-nums">
      {label} <span className="font-black text-slate-800">{value}</span>
    </span>
  );
}
